import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

// 💰 Деньги по сделке (04.11.2026, владелец: «маржа — полная экосистема»). План расходов считает сервер торгов (costs.py) —
// здесь записываем факт: покупка, расходы по статьям, продажа → маржа, срок оборота, план/факт. Своё время не считаем.
const r0 = v => v == null ? '—' : Math.round(Number(v)).toLocaleString('ru-RU') + ' ₽';
const muted = { fontSize: 12.5, color: 'var(--text-muted)' };
const today = () => new Date().toISOString().slice(0, 10);
const fd = s => s ? String(s).slice(0, 10).split('-').reverse().join('.') : '';

function Stat({ label, value, hint, color }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', minWidth: 120, flex: '1 1 120px' }}>
      <div style={muted}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color }}>{value}</div>
      {hint && <div style={muted}>{hint}</div>}
    </div>
  );
}

export default function TradeMoney({ id, showToast }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [f, setF] = useState({ kind: 'cost', cat: 'fuel', amount: '', op_date: today(), note: '' });
  const load = useCallback(() => api.get(`/trades/${id}/money`).then(r => { setD(r.data); setErr(''); })
    .catch(e => setErr(e.response?.data?.error || e.message)), [id]);
  useEffect(() => { load(); }, [load]);

  const add = async body => {
    try { await api.post(`/trades/${id}/money`, body); showToast('Записано'); load(); return true; }
    catch (e) { showToast(e.response?.data?.error || 'Не записалось', 'error'); return false; }
  };
  const del = async m => {
    if (!window.confirm('Удалить запись?')) return;
    try { await api.delete(`/trades/money/${m.id}`); load(); } catch (e) { showToast(e.response?.data?.error || 'Не удалилось', 'error'); }
  };

  if (err) return <div className="card" style={{ color: 'var(--red)' }}>{err}</div>;
  if (!d) return <div className="card">загрузка…</div>;
  const s = d.summary, labels = d.labels || {}, plan = d.plan || { items: [] };
  const lab = k => labels[k] || k;
  const cats = [...new Set([...(plan.items || []).map(i => i.key), 'fuel', 'train', 'food', 'night', 'battery', 'reserve', 'other'])];
  const hasBuy = d.rows.some(r => r.kind === 'buy'), hasSell = d.rows.some(r => r.kind === 'sell');

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <Stat label="Вложено" value={r0(s.invested)} hint={`покупка ${r0(s.buy)} + расходы ${r0(s.cost)}`} />
        <Stat label="Продажа" value={s.sale ? r0(s.sale) : 'ещё нет'}
          hint={[s.sell_date ? fd(s.sell_date) : '', s.resale_plan ? `оценка при покупке ${r0(s.resale_plan)}${s.sale ? ` (${Math.round(s.sale / s.resale_plan * 100)}%)` : ''}` : ''].filter(Boolean).join(' · ')}
          color={s.sale && s.resale_plan ? (s.sale >= s.resale_plan * 0.95 ? 'var(--green)' : s.sale < s.resale_plan * 0.8 ? 'var(--red)' : undefined) : undefined} />
        <Stat label="НДФЛ (оценка)" value={s.tax == null ? '—' : r0(s.tax)} hint="13% с (продажа − покупка); вычет 250 000 ₽ — один на год" />
        <Stat label="Маржа" value={s.profit == null ? '—' : r0(s.profit)} color={s.profit == null ? undefined : s.profit >= 0 ? 'var(--green)' : 'var(--red)'}
          hint={s.margin_pct != null ? `${s.margin_pct}% к вложенному` : 'после продажи'} />
        <Stat label="Срок оборота" value={s.days != null ? `${s.days} дн.` : '—'} hint={s.buy_date ? `куплено ${fd(s.buy_date)}` : ''} />
      </div>

      {(!hasBuy || !hasSell) && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12, padding: 10, background: 'var(--accent-light)', borderRadius: 'var(--radius-sm)' }}>
          <b>{hasBuy ? '🏁 Продал за' : '🛒 Купил за'}</b>
          <input className="form-input" style={{ width: 150 }} placeholder="сумма, ₽" value={f.kind === (hasBuy ? 'sell' : 'buy') ? f.amount : ''}
            onChange={e => setF({ ...f, kind: hasBuy ? 'sell' : 'buy', amount: e.target.value })} />
          <input className="form-input" type="date" style={{ width: 160 }} value={f.op_date} onChange={e => setF({ ...f, op_date: e.target.value })} />
          <button className="btn btn-primary btn-sm" onClick={async () => {
            if (await add({ kind: hasBuy ? 'sell' : 'buy', amount: f.amount, op_date: f.op_date })) setF({ ...f, kind: 'cost', amount: '' });
          }}>Записать</button>
          {!hasBuy && <span style={muted}>с этой суммой зафиксируется план расходов — потом сравним с фактом</span>}
        </div>
      )}

      <div style={{ fontWeight: 700, margin: '6px 0' }}>📊 Расходы: план и факт {d.fixed ? <span style={muted}>(план зафиксирован при покупке)</span> : <span style={muted}>(план на сейчас — зафиксируется при записи покупки)</span>}</div>
      {d.error && <div style={{ ...muted, color: 'var(--red)' }}>{d.error}</div>}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
          <thead><tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}><th>Статья</th><th>План</th><th>Факт</th><th></th></tr></thead>
          <tbody>
            {s.vs.map(v => {
              const p = (plan.items || []).find(i => i.key === v.key);
              const over = v.fact && v.plan && v.fact > v.plan * 1.2;
              return (
                <tr key={v.key} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '6px 4px' }}>{v.label || lab(v.key)}{p && <div style={muted}>{p.how}</div>}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{v.plan ? r0(v.plan) : '—'}</td>
                  <td style={{ whiteSpace: 'nowrap', color: over ? 'var(--red)' : undefined, fontWeight: v.fact ? 700 : 400 }}>{v.fact ? r0(v.fact) : '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {v.plan > 0 && !v.fact && <button className="btn btn-secondary btn-sm" title="записать как в плане" onClick={() => add({ kind: 'cost', cat: v.key, amount: v.plan, op_date: today(), note: 'как в плане' })}>✓ как в плане</button>}
                    <button className="btn btn-secondary btn-sm" style={{ marginLeft: 4 }} onClick={() => setF({ ...f, kind: 'cost', cat: v.key, amount: '' })}>✎ своя сумма</button>
                  </td>
                </tr>);
            })}
            <tr style={{ fontWeight: 700 }}><td style={{ padding: '6px 4px' }}>Итого</td><td>{r0(s.plan_total)}</td><td>{r0(s.cost)}</td><td></td></tr>
          </tbody>
        </table>
      </div>
      {(plan.notes || []).map((n, i) => <div key={i} style={muted}>ℹ {n}</div>)}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', margin: '12px 0' }}>
        <b>+ Расход</b>
        <select className="form-input" style={{ width: 210 }} value={f.cat} onChange={e => setF({ ...f, kind: 'cost', cat: e.target.value })}>
          {cats.map(k => <option key={k} value={k}>{lab(k)}</option>)}
        </select>
        <input className="form-input" style={{ width: 130 }} placeholder="сумма, ₽" value={f.kind === 'cost' ? f.amount : ''} onChange={e => setF({ ...f, kind: 'cost', amount: e.target.value })} />
        <input className="form-input" type="date" style={{ width: 160 }} value={f.op_date} onChange={e => setF({ ...f, op_date: e.target.value })} />
        <input className="form-input" style={{ flex: 1, minWidth: 140 }} placeholder="заметка (необязательно)" value={f.note} onChange={e => setF({ ...f, note: e.target.value })} />
        <button className="btn btn-primary btn-sm" onClick={async () => {
          if (await add({ kind: 'cost', cat: f.cat, amount: f.amount, op_date: f.op_date, note: f.note })) setF({ ...f, amount: '', note: '' });
        }}>Записать</button>
      </div>

      {d.rows.length > 0 && <details open={d.rows.length <= 8}>
        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Все записи ({d.rows.length})</summary>
        {d.rows.map(m => (
          <div key={m.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '4px 0', borderTop: '1px solid var(--border)', fontSize: 13.5, flexWrap: 'wrap' }}>
            <span style={{ minWidth: 80, ...muted }}>{fd(m.op_date) || 'без даты'}</span>
            <span style={{ flex: 1, minWidth: 140 }}>{m.kind === 'buy' ? '🛒 Покупка' : m.kind === 'sell' ? '🏁 Продажа' : lab(m.cat)}{m.note ? <span style={muted}> · {m.note}</span> : ''}</span>
            <b>{r0(m.amount)}</b>
            <button className="btn-icon" title="Удалить" onClick={() => del(m)}>✕</button>
          </div>))}
      </details>}
      <div style={{ ...muted, marginTop: 8 }}>Своё время не считаем. По закрытым сделкам факт автоматически поправляет план расходов и оценку «продадим за» для новых лотов (от 2 сделок).</div>
    </div>
  );
}
